import {
    OptimizationObjective,
    OptimizationObjectiveSubRow,
} from '~/domain/patient/optimizationObjective';

// DTO types for the frontend export/import format (root array).
// Used by parseOptimizationObjectives (and the existing tests).
type ExportSubRowDto = {
    type: string;
    volumePercent: number;
    doseGy: number;
    priority: number;
    gEUDa: number;
};

type ExportObjectiveDto = {
    structure: string;
    volumePercent: number;
    doseGy: number;
    priority: number;
    gEUDa: number;
    constraints: ExportSubRowDto[];
};

// DTO types for the backend optimization_objectives.json format.
// The backend returns a root object whose `Objectives` field is a flat list,
// one entry per constraint, grouped by ROI.
type BackendObjectiveDto = {
    Index: number;
    ROI: string;
    Type: string;
    Dose: number;
    Volume: number;
    /** Optimisation weight; mapped to the domain `priority` field. */
    Weight?: number;
    /** gEUD exponent; mapped to the domain `gEUD` field. */
    a?: number;
    /** True for control structures (rings, skin) used to shape dose falloff. */
    IsControl?: boolean;
};

type BackendOptimizationObjectivesJson = {
    Objectives: BackendObjectiveDto[];
    'Controls objectives'?: BackendObjectiveDto[];
};

export const OPTIMIZATION_OBJECTIVES_FILENAME = 'optimization_objectives.json';

export function extractOptimizationObjectivesFile(
    files: File[]
): File | undefined {
    return files.find((f) => f.name === OPTIMIZATION_OBJECTIVES_FILENAME);
}

export async function readOptimizationObjectives(
    file: File
): Promise<OptimizationObjective[]> {
    const text = await file.text();
    const json = JSON.parse(text);
    return parseBackendOptimizationObjectives(json);
}

/**
 * Parses the backend optimization_objectives.json format.
 * The backend returns a root object with an `Objectives` array where each
 * entry is a flat constraint. Entries are grouped by ROI to produce
 * parent rows with sub-rows for the table display.
 */
export function parseBackendOptimizationObjectives(
    json: unknown
): OptimizationObjective[] {
    if (
        typeof json !== 'object' ||
        json === null ||
        !Array.isArray((json as BackendOptimizationObjectivesJson).Objectives)
    ) {
        return [];
    }

    const typed = json as BackendOptimizationObjectivesJson;
    const objectives = typed.Objectives;
    const controlObjectives: BackendObjectiveDto[] = Array.isArray(
        typed['Controls objectives']
    )
        ? typed['Controls objectives'].map((c) => ({
              ...c,
              IsControl: true,
          }))
        : [];

    const allObjectives = [...objectives, ...controlObjectives];

    // Preserve the order in which ROIs first appear.
    const roiOrder: string[] = [];
    const byRoi = new Map<string, BackendObjectiveDto[]>();
    for (const obj of allObjectives) {
        if (!byRoi.has(obj.ROI)) {
            roiOrder.push(obj.ROI);
            byRoi.set(obj.ROI, []);
        }
        byRoi.get(obj.ROI)!.push(obj);
    }

    return roiOrder.map((roi, index): OptimizationObjective => {
        const constraints = byRoi.get(roi)!;
        return {
            id: index + 1,
            structure: roi,
            dose: 0,
            volumePercent: 0,
            priority: 0,
            gEUD: 0,
            subRows: constraints.map(
                (c): OptimizationObjectiveSubRow => ({
                    type: c.Type,
                    dose: c.Dose,
                    volumePercent: c.Volume,
                    priority: c.Weight ?? 0,
                    gEUD: c.a ?? 0,
                    isControl: c.IsControl ?? false,
                })
            ),
        };
    });
}

/**
 * Parses the frontend export/import format (root array).
 * Kept for compatibility with the JSON export produced by this application.
 */
export function parseOptimizationObjectives(
    json: unknown
): OptimizationObjective[] {
    if (!Array.isArray(json)) {
        return [];
    }

    return (json as ExportObjectiveDto[]).map(
        (item, index): OptimizationObjective => ({
            id: index + 1,
            structure: item.structure,
            dose: item.doseGy,
            volumePercent: item.volumePercent,
            priority: item.priority,
            gEUD: item.gEUDa,
            subRows: (item.constraints ?? []).map(
                (c): OptimizationObjectiveSubRow => ({
                    type: c.type,
                    dose: c.doseGy,
                    volumePercent: c.volumePercent,
                    priority: c.priority,
                    gEUD: c.gEUDa,
                    isControl: false,
                })
            ),
        })
    );
}
